import { useEffect, useState } from 'react';
import { RunnerProfile, saveRunnerProfile } from '../api/client';
import { haptic } from '../lib/telegram';
import './RunnerProfileCard.css';

interface RunnerProfileCardProps {
  userId: number;
  profile: RunnerProfile | null;
  onSaved: (profile: RunnerProfile) => void;
}

const experienceLabel = {
  beginner: 'Начинающий',
  intermediate: 'Регулярный',
  advanced: 'Опытный'
};

export const RunnerProfileCard: React.FC<RunnerProfileCardProps> = ({ userId, profile, onSaved }) => {
  const [editing, setEditing] = useState(!profile);
  const [age, setAge] = useState(profile?.age ? String(profile.age) : '');
  const [weight, setWeight] = useState(profile?.weight_kg ? String(profile.weight_kg) : '');
  const [sex, setSex] = useState<RunnerProfile['sex']>(profile?.sex || 'male');
  const [experience, setExperience] = useState<RunnerProfile['experience']>(profile?.experience || 'beginner');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile) return;
    setAge(String(profile.age));
    setWeight(String(profile.weight_kg));
    setSex(profile.sex);
    setExperience(profile.experience);
  }, [profile]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveRunnerProfile(userId, {
        age: Number(age),
        weight_kg: Number(weight.replace(',', '.')),
        sex,
        experience
      });
      onSaved(saved);
      setEditing(false);
      haptic.success();
    } catch (err: any) {
      setError(err?.message || 'Не удалось сохранить профиль');
      haptic.error();
    } finally {
      setSaving(false);
    }
  };

  if (!editing && profile) {
    return (
      <section className="runner-profile-card compact">
        <div>
          <span className="profile-kicker">ПРОФИЛЬ СКОРИНГА</span>
          <h2>Персональные ориентиры</h2>
          <p>{profile.age} лет · {profile.weight_kg} кг · {experienceLabel[profile.experience]}</p>
        </div>
        <button onClick={() => setEditing(true)}>Изменить</button>
      </section>
    );
  }

  return (
    <section className="runner-profile-card">
      <span className="profile-kicker">ПРОФИЛЬ СКОРИНГА</span>
      <h2>{profile ? 'Обновить параметры' : 'Сделаем оценку персональной'}</h2>
      <p>Параметры используются только для диапазонов нагрузки и техники.</p>
      <div className="profile-form">
        <label><span>Возраст</span><input inputMode="numeric" value={age} onChange={event => setAge(event.target.value)} placeholder="35" /></label>
        <label><span>Вес, кг</span><input inputMode="decimal" value={weight} onChange={event => setWeight(event.target.value)} placeholder="75" /></label>
        <label>
          <span>Пол</span>
          <select value={sex} onChange={event => setSex(event.target.value as RunnerProfile['sex'])}>
            <option value="male">Мужской</option>
            <option value="female">Женский</option>
            <option value="other">Не указывать</option>
          </select>
        </label>
        <label>
          <span>Опыт</span>
          <select value={experience} onChange={event => setExperience(event.target.value as RunnerProfile['experience'])}>
            <option value="beginner">До 1 года</option>
            <option value="intermediate">1–3 года</option>
            <option value="advanced">Более 3 лет</option>
          </select>
        </label>
      </div>
      {error && <p className="profile-error">{error}</p>}
      <div className="profile-actions">
        <button className="profile-save" onClick={save} disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить профиль'}</button>
        {profile && <button className="profile-cancel" onClick={() => setEditing(false)}>Отмена</button>}
      </div>
    </section>
  );
};
